import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errText } from "../../lib/api";
import { Badge, CustomerLink, DataTable, OrderLink, RiskDot, Select, ShopLink, money, shortDate, statusTone,
         useFilterOptions, useSelection, useTable } from "./ui";
import OrderDrawer from "./OrderDrawer";
import MobileOrderCard from "./MobileOrderCard";
import { ShareBriefModal } from "./ShareBrief";
import { Download, Bot } from "lucide-react";

// How the card hold reads in the list: held money is the one ops must chase.
const HOLD_LABEL = { authorized: "held", captured: "charged", released: "released", pending: "unpaid" };
const HOLD_TONE = { authorized: "warn", captured: "good", released: "ink", pending: "bad" };

export default function OrdersPage() {
  const table = useTable("/admin/tables/orders");
  const options = useFilterOptions();
  const selection = useSelection();
  const [sp, setSp] = useSearchParams();
  const openId = sp.get("order");
  const [bulk, setBulk] = useState(null); // { action, ... }
  const [shareOrder, setShareOrder] = useState(null);
  const [msg, setMsg] = useState("");

  const closeDrawer = () => {
    const next = Object.fromEntries(sp.entries());
    delete next.order;
    setSp(next, { replace: true });
  };

  const runBulk = async (payload) => {
    setMsg("");
    try {
      const { data } = await api.post("/admin/orders/bulk", { ids: selection.ids, ...payload });
      setMsg(`${data.changed}/${data.total} orders updated.`);
      selection.clear();
      table.reload();
      setBulk(null);
    } catch (e) { setMsg(errText(e)); }
  };

  const exportSelection = () => {
    const rows = selection.rows;
    if (!rows.length) return;
    const cols = ["order_number", "status", "delivery_date", "city", "recipient_name", "buyer_email",
                  "total_mad", "gateway_fee_mad", "payout_mad", "gateway", "payment_state"];
    const header = ["Order", "Status", "Delivery", "City", "Recipient", "Buyer", "Total MAD",
                    "Card fee MAD", "Payout MAD", "Gateway", "Payment"];
    const csv = [header, ...rows.map((r) => cols.map((c) => {
      const v = c === "order_number" ? r.order_number : c.includes(".") ? "" : r[c];
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }))].map((row) => row.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `orders-selection-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const columns = [
    { key: "order_number", label: "Order", sortable: true, render: (r) => (
      <span className="whitespace-nowrap">
        <RiskDot level={r.risk} /> <OrderLink number={r.order_number} id={r.id} testid={`order-link-${r.order_number}`} />
        {r.source === "agentic" ? (
          <Badge tone="brass" className="ml-1.5" testid={`agent-order-${r.order_number}`} title={r.agent_name || "AI agent"}>
            <Bot size={11} className="inline -mt-0.5 mr-0.5" />via AI agent
          </Badge>
        ) : null}
      </span>
    ) },
    { key: "status", label: "Status", sortable: true, render: (r) => (
      <span className="flex gap-1 flex-wrap">
        <Badge tone={statusTone(r.status)}>{r.status === "awaiting_acceptance" ? "awaiting acceptance" : r.status}</Badge>
        {r.acceptance_overdue ? <Badge tone="bad" testid={`overdue-${r.id}`}>overdue</Badge> : null}
      </span>
    ) },
    { key: "delivery_date", label: "Delivery", sortable: true, render: (r) => <span className="tabular whitespace-nowrap">{shortDate(r.delivery_date)}</span> },
    { key: "city", label: "City" },
    { key: "florist", label: "Florist", render: (r) => <ShopLink florist={r.florist} /> },
    { key: "buyer_email", label: "Buyer", render: (r) => <span className="text-xs"><CustomerLink email={r.buyer_email} /></span> },
    { key: "recipient_name", label: "Recipient", render: (r) => <span className="text-xs">{r.recipient_name}</span> },
    { key: "total_mad", label: "Total", sortable: true, render: (r) => <span className="tabular whitespace-nowrap">{money(r.total_mad)} MAD</span> },
    { key: "gateway", label: "Gateway", render: (r) => <Badge tone="ink">{r.gateway}</Badge> },
    { key: "payment_state", label: "Card", render: (r) => (
      <Badge tone={HOLD_TONE[r.payment_state] || "ink"} testid={`hold-${r.order_number}`}>
        {HOLD_LABEL[r.payment_state] || r.payment_state}
      </Badge>
    ) },
    { key: "payout_mad", label: "Payout", sortable: true, render: (r) => (
      <span className="tabular whitespace-nowrap text-xs">{money(r.payout_mad)} <span className="text-muted">−{money(r.gateway_fee_mad)} fee</span></span>
    ) },
  ];

  const filters = (
    <>
      <Select testid="orders-filter-scope" placeholder="Everything" value={table.params.scope} onChange={(v) => table.set({ scope: v })}
              options={[{ value: "live", label: "Live orders" }, { value: "today", label: "Delivering today" },
                        { value: "at_risk", label: "At risk" }, { value: "unassigned", label: "Unassigned" },
                        { value: "stuck_holds", label: "Stuck holds (accepted, never charged)" },
                        { value: "recurring", label: "From a plan" }]} />
      <Select testid="orders-filter-status" placeholder="Any status" value={table.params.status} onChange={(v) => table.set({ status: v })}
              options={options.order_statuses} />
      <Select testid="orders-filter-payment-state" placeholder="Any card state" value={table.params.payment_state}
              onChange={(v) => table.set({ payment_state: v })} options={options.payment_states || []} />
      <Select testid="orders-filter-city" placeholder="Any city" value={table.params.city_id} onChange={(v) => table.set({ city_id: v })}
              options={options.cities.map((c) => ({ value: c.id, label: c.name }))} />
      <Select testid="orders-filter-florist" placeholder="Any shop" value={table.params.florist_id} onChange={(v) => table.set({ florist_id: v })}
              options={options.florists.map((f) => ({ value: f.id, label: f.name }))} />
      <Select testid="orders-filter-gateway" placeholder="Any gateway" value={table.params.gateway} onChange={(v) => table.set({ gateway: v })}
              options={options.gateways} />
      <input type="date" className="ctl" data-testid="orders-filter-from" value={table.params.date_from || ""} onChange={(e) => table.set({ date_from: e.target.value })} />
      <input type="date" className="ctl" data-testid="orders-filter-to" value={table.params.date_to || ""} onChange={(e) => table.set({ date_to: e.target.value })} />
    </>
  );

  const bulkBar = (
    <>
      <button className="text-xs px-3 py-1 border border-plaster/40 hover:bg-plaster/10" data-testid="orders-bulk-nudge" onClick={() => setBulk({ action: "nudge" })}>
        Nudge florist
      </button>
      <button className="text-xs px-3 py-1 border border-plaster/40 hover:bg-plaster/10" data-testid="orders-bulk-reassign" onClick={() => setBulk({ action: "reassign" })}>
        Reassign to shop
      </button>
      <button className="text-xs px-3 py-1 border border-plaster/40 hover:bg-plaster/10" data-testid="orders-bulk-note" onClick={() => setBulk({ action: "note" })}>
        Add ops note
      </button>
      <button className="text-xs px-3 py-1 border border-plaster/40 hover:bg-plaster/10" data-testid="orders-bulk-resolve" onClick={() => setBulk({ action: "resolve" })}>
        Mark resolved
      </button>
      <button className="text-xs px-3 py-1 border border-plaster/40 hover:bg-plaster/10" data-testid="orders-bulk-cancel" onClick={() => setBulk({ action: "cancel" })}>
        Cancel
      </button>
      <button className="text-xs px-3 py-1 border border-plaster/40 hover:bg-plaster/10 inline-flex items-center gap-1" data-testid="orders-bulk-csv" onClick={exportSelection}>
        <Download size={12} /> Export selection
      </button>
    </>
  );

  return (
    <>
      {msg ? <p className="text-sm text-brass mb-3" data-testid="orders-message">{msg}</p> : null}
      <DataTable table={table} columns={columns} testid="orders-table" name="orders" filters={filters}
                 searchPlaceholder="Order, email, recipient…" empty="No order matches those filters."
                 selection={selection} bulkBar={bulkBar}
                 mobileRow={(r) => (
                   <MobileOrderCard r={r} onChanged={table.reload}
                                    onShare={(row, accepted) => setShareOrder({ ...row, accepted: !!accepted })}
                                    hold={{ tone: HOLD_TONE[r.payment_state] || "ink",
                                            label: HOLD_LABEL[r.payment_state] || r.payment_state }} />
                 )} />
      {shareOrder ? (
        <ShareBriefModal orderId={shareOrder.id} orderNumber={shareOrder.order_number} accepted={shareOrder.accepted}
                         onClose={() => setShareOrder(null)} />
      ) : null}
      {bulk ? <BulkModal bulk={bulk} onClose={() => setBulk(null)} onRun={runBulk} shops={options.florists} count={selection.ids.length} /> : null}
      {openId ? <OrderDrawer orderId={openId} onClose={closeDrawer} onChanged={table.reload} /> : null}
    </>
  );
}

function BulkModal({ bulk, onClose, onRun, shops, count }) {
  const [floristId, setFloristId] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const titles = {
    reassign: "Reassign these orders", nudge: "Send a nudge to the current shop",
    note: "Add an ops note to each order", resolve: "Mark issues resolved",
    cancel: "Cancel these orders",
  };
  const submit = () => {
    if (bulk.action === "reassign" && !floristId) return;
    if (bulk.action === "cancel" && reason.trim().length < 3) return;
    if (bulk.action === "note" && note.trim().length < 2) return;
    onRun({ action: bulk.action, florist_id: floristId, reason, note });
  };
  return (
    <div className="fixed inset-0 scrim z-40 flex items-center justify-center p-6" onClick={onClose} data-testid="orders-bulk-modal">
      <div className="bg-plaster w-full max-w-md p-6 border border-brass space-y-3" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-xl text-ink">{titles[bulk.action]}</h2>
        <p className="text-xs text-muted">{count} order{count === 1 ? "" : "s"} selected.</p>
        {bulk.action === "reassign" ? (
          <select className="field" data-testid="bulk-florist-select" value={floristId} onChange={(e) => setFloristId(e.target.value)}>
            <option value="">Choose a shop</option>
            {shops.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        ) : null}
        {["reassign", "nudge", "cancel"].includes(bulk.action) ? (
          <textarea className="field min-h-[80px]" data-testid="bulk-reason" placeholder={bulk.action === "cancel" ? "Reason (required, visible to buyer)" : "Reason (optional)"} value={reason} onChange={(e) => setReason(e.target.value)} />
        ) : null}
        {["note", "resolve"].includes(bulk.action) ? (
          <textarea className="field min-h-[80px]" data-testid="bulk-note" placeholder={bulk.action === "note" ? "Note to the shop / event log" : "Resolution note"} value={note} onChange={(e) => setNote(e.target.value)} />
        ) : null}
        <div className="flex gap-2">
          <button className="btn-primary flex-1" data-testid="bulk-run" onClick={submit}>Run on {count} order{count === 1 ? "" : "s"}</button>
          <button className="btn-ghost text-xs" data-testid="bulk-cancel-btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
